import { Component, Element, h, Prop, State, Watch } from '@stencil/core'
import type { Account } from '../utils/api'
import type { SelectedAccount } from '../utils/storage'

@Component({
  tag: 'account-combobox',
  styleUrl: 'AccountCombobox.css',
  shadow: false
})
export class AccountCombobox {
  @Element() el: HTMLElement

  @Prop() accounts: Account[] = []
  @Prop() selectedAccount: SelectedAccount | null = null
  @Prop() isLoading: boolean = false
  @Prop() favoriteAccounts: string[] = []
  @Prop() onSelectAccount: (accountId: string) => void
  @Prop() onToggleFavorite: (accountId: string) => void

  @State() searchQuery: string = ''
  @State() debouncedSearchQuery: string = ''
  @State() highlightedIndex: number = -1
  @State() showFavoritesOnly: boolean = false

  private dialogRef: HTMLDialogElement
  private searchInputRef: HTMLInputElement
  private searchDebounceTimer: ReturnType<typeof setTimeout> | null = null

  disconnectedCallback() {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer)
    }
  }

  @Watch('searchQuery')
  handleSearchQueryChange() {
    this.highlightedIndex = -1
  }

  private getFilteredAccounts(): Account[] {
    let accountsToFilter = this.accounts

    // Filter by favorites if enabled
    if (this.showFavoritesOnly) {
      accountsToFilter = this.accounts.filter(account => this.favoriteAccounts.includes(account.id))
    }

    // Then filter by search query
    const query = this.debouncedSearchQuery.toLowerCase().trim()
    if (!query) return accountsToFilter

    return accountsToFilter.filter(account => {
      const nameMatch = account.name.toLowerCase().includes(query)
      const idMatch = account.id.toLowerCase().includes(query)
      return nameMatch || idMatch
    })
  }

  private handleOpenModal = () => {
    if (this.isLoading) return
    this.dialogRef?.showModal()
    setTimeout(() => {
      this.searchInputRef?.focus()
    }, 0)
  }

  private handleCloseModal = () => {
    this.dialogRef?.close()
    this.searchQuery = ''
    this.debouncedSearchQuery = ''
    this.highlightedIndex = -1
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer)
    }
  }

  private handleSelectAccount = (accountId: string) => {
    this.onSelectAccount?.(accountId)
    this.handleCloseModal()
  }

  private handleSearchInput = (e: Event) => {
    const value = (e.target as HTMLInputElement).value
    this.searchQuery = value

    // Debounce the search query update
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer)
    }

    this.searchDebounceTimer = setTimeout(() => {
      this.debouncedSearchQuery = value
    }, 300)
  }

  private handleKeyDown = (e: KeyboardEvent) => {
    const filteredAccounts = this.getFilteredAccounts()

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        this.highlightedIndex = Math.min(this.highlightedIndex + 1, filteredAccounts.length - 1)
        this.scrollToHighlighted()
        break
      case 'ArrowUp':
        e.preventDefault()
        this.highlightedIndex = Math.max(this.highlightedIndex - 1, 0)
        this.scrollToHighlighted()
        break
      case 'Enter':
        e.preventDefault()
        if (this.highlightedIndex >= 0 && filteredAccounts[this.highlightedIndex]) {
          this.handleSelectAccount(filteredAccounts[this.highlightedIndex].id)
        }
        break
      case 'Escape':
        e.preventDefault()
        this.handleCloseModal()
        break
    }
  }

  private scrollToHighlighted() {
    const optionsList = this.dialogRef?.querySelector('.account-combobox-options')
    if (!optionsList) return
    const highlightedElement = optionsList.querySelector(`[data-index="${this.highlightedIndex}"]`) as HTMLElement
    if (highlightedElement) {
      highlightedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    }
  }

  private handleToggleFavorite = (e: Event, accountId: string) => {
    e.stopPropagation()
    this.onToggleFavorite?.(accountId)
  }

  private handleDialogClick = (e: MouseEvent) => {
    // Close dialog when clicking on backdrop
    if (e.target === this.dialogRef) {
      this.handleCloseModal()
    }
  }

  private handleToggleFavoritesFilter = () => {
    this.showFavoritesOnly = !this.showFavoritesOnly
    this.highlightedIndex = -1
  }

  private renderFavoriteIcon(isFavorite: boolean) {
    return (
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill={isFavorite ? 'currentColor' : 'none'}
        stroke="currentColor"
        stroke-width="2"
      >
        <title>Favorite icon</title>
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
      </svg>
    )
  }

  render() {
    const filteredAccounts = this.getFilteredAccounts()
    const selectedAccountData = this.accounts.find(acc => acc.id === this.selectedAccount?.id)

    return (
      <div class="account-combobox">
        <button
          type="button"
          class="account-combobox-trigger"
          onClick={this.handleOpenModal}
          disabled={this.isLoading}
        >
          <span class="account-combobox-trigger-text">{selectedAccountData ? selectedAccountData.name : 'Select account...'}</span>
          <svg
            class="account-combobox-trigger-icon"
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <title>Open modal icon</title>
            <path d="M3 4.5L6 7.5L9 4.5" />
          </svg>
        </button>

        {/* biome-ignore lint/a11y/useKeyWithClickEvents: dialog element has native Escape key handling */}
        <dialog
          ref={el => {
            this.dialogRef = el
          }}
          class="account-combobox-dialog"
          onClick={this.handleDialogClick}
        >
          <div class="account-combobox-modal">
            <div class="account-combobox-modal-header">
              <h3 class="account-combobox-modal-title">Select Account</h3>
              <div class="account-combobox-header-actions">
                <label class="account-combobox-toggle">
                  <input
                    type="checkbox"
                    class="account-combobox-toggle-input"
                    checked={this.showFavoritesOnly}
                    onChange={this.handleToggleFavoritesFilter}
                  />
                  <span class="account-combobox-toggle-slider"></span>
                  <span class="account-combobox-toggle-label">Favorites only</span>
                </label>
                <button
                  type="button"
                  class="account-combobox-close-btn"
                  onClick={this.handleCloseModal}
                  title="Close"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <title>Close icon</title>
                    <path d="M5 5L15 15M15 5L5 15" />
                  </svg>
                </button>
              </div>
            </div>

            <div class="account-combobox-search-wrapper">
              <svg
                class="account-combobox-search-icon"
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <title>Search icon</title>
                <circle
                  cx="6"
                  cy="6"
                  r="4"
                />
                <path d="M9 9L12 12" />
              </svg>
              <input
                ref={el => {
                  this.searchInputRef = el
                }}
                type="text"
                class="account-combobox-search"
                placeholder="Search account..."
                value={this.searchQuery}
                onInput={this.handleSearchInput}
                onKeyDown={this.handleKeyDown}
              />
            </div>

            <div
              class="account-combobox-options"
              role="listbox"
            >
              {filteredAccounts.length === 0 ? (
                <div class="account-combobox-empty">{this.showFavoritesOnly ? 'No favorites found. Add favorites by clicking the star icon.' : 'No accounts found'}</div>
              ) : (
                filteredAccounts.map((account, index) => {
                  const isSelected = this.selectedAccount?.id === account.id
                  const isHighlighted = this.highlightedIndex === index
                  const isFavorite = this.favoriteAccounts?.includes(account.id)

                  return (
                    // can not use button here cause button inside a button is not allowed
                    // biome-ignore lint/a11y/useKeyWithClickEvents: option element has native Enter key handling
                    <div
                      key={account.id}
                      data-index={index}
                      class={`account-combobox-option ${isSelected ? 'selected' : ''} ${isHighlighted ? 'highlighted' : ''}`}
                      role="option"
                      aria-selected={isSelected}
                      onClick={() => this.handleSelectAccount(account.id)}
                      tabIndex={0}
                    >
                      <div class="account-combobox-option-content">
                        <div class="account-combobox-option-name">{account.name}</div>
                        <div class="account-combobox-option-id">{account.id}</div>
                      </div>
                      <button
                        type="button"
                        class={`account-combobox-favorite-btn ${isFavorite ? 'active' : ''}`}
                        onClick={e => this.handleToggleFavorite(e, account.id)}
                        title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                      >
                        {this.renderFavoriteIcon(isFavorite)}
                      </button>
                    </div>
                  )
                })
              )}
            </div>
          </div>
        </dialog>
      </div>
    )
  }
}
